import React, { useState, useEffect } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorWhatThisMeans from './calc_components/CalculatorWhatThisMeans';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { loanFromEmi } from './homeLoanAffordabilityMath';
import './calculators.css';
import './EmiInputBands.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 6;
const maxInterestRate = 50;
const defaultInterestRate = 9;
const interestRateStep = 0.25;

const minTenureYears = 1;
const maxTenureYears = 30;
const defaultTenureYears = 20;

const minEmi = 0;
const maxEmi = 5000000;
const defaultEmi = 10000;
const emiStep = 1000;

const STANDALONE_DEFAULTS = {
  interestRate: defaultInterestRate,
  tenureYears: defaultTenureYears,
  monthlyEmi: defaultEmi,
};

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return defaultInterestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(maxInterestRate, Math.max(minInterestRate, Number(snapped.toFixed(2))));
}

export default function LoanAmountCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const [interestRate, setInterestRate] = useState(STANDALONE_DEFAULTS.interestRate);
  const [tenureYears, setTenureYears] = useState(STANDALONE_DEFAULTS.tenureYears);
  const [monthlyEmi, setMonthlyEmi] = useState(STANDALONE_DEFAULTS.monthlyEmi);
  const [emiForWords, setEmiForWords] = useState(STANDALONE_DEFAULTS.monthlyEmi);

  const loanAmount = loanFromEmi(monthlyEmi, interestRate, tenureYears);
  const emiWords =
    emiForWords > 0 ? inrAmountInWordsShort(Math.round(emiForWords)) : '';

  function handleStartOver(e) {
    e.preventDefault();
    setInterestRate(STANDALONE_DEFAULTS.interestRate);
    setTenureYears(STANDALONE_DEFAULTS.tenureYears);
    setMonthlyEmi(STANDALONE_DEFAULTS.monthlyEmi);
    setEmiForWords(STANDALONE_DEFAULTS.monthlyEmi);
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Loan Amount Calculator</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          Estimate the loan principal supported by a monthly EMI, interest rate, and tenure.
        </p>
      </div>

      <LoanAmountResultPanel loanAmount={loanAmount} />

      <div className="border-top-light nps-ups-divider" />

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="loan-amt-interest-rate"
          label="Interest rate"
          tooltip="Annual interest rate on the loan. Illustrative only—actual rates vary by lender and product."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.interestRate}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="loan-amt-tenure-years"
          label="Tenure in years"
          tooltip="Total repayment period for the loan, in whole years."
          value={tenureYears}
          setValue={setTenureYears}
          min={minTenureYears}
          max={maxTenureYears}
          step={1}
          format={formatYearsCount}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.tenureYears}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="loan-amt-monthly-emi"
            label="Monthly EMI"
            tooltip="Equated monthly instalment you can pay. Used to back out the loan amount."
            value={Math.min(monthlyEmi, maxEmi)}
            setValue={(v) => {
              const next = Math.min(maxEmi, Math.max(0, v));
              setMonthlyEmi(next);
              setEmiForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setEmiForWords(Math.min(maxEmi, Math.max(0, n)));
              } else {
                setEmiForWords(0);
              }
            }}
            min={minEmi}
            max={maxEmi}
            step={emiStep}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.monthlyEmi}
          />
          {emiWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {emiWords}
            </p>
          ) : null}
        </div>
      </section>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative loan amount from the standard amortisation formula; excludes processing
        fees, insurance, prepayment, and tax effects. Not lender or product advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="loan-amount-calculator" />
      </div>
    </div>
  );
}

function LoanAmountResultPanel({ loanAmount }) {
  const [showResult, setShowResult] = useState(true);
  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();
  const loanDisplay = formatInr(loanAmount);
  const wordsLine =
    Number.isFinite(loanAmount) && loanAmount > 0
      ? inrAmountInWordsShort(resolveAmount(loanAmount))
      : '';

  return (
    <div className="grid-center-display-result nps-ups-result-panel nps-ups-result-outer calc-result-legacy nps-result-legacy">
      <CalculatorPrecisionToggle />
      <button
        type="button"
        className="btn-small-round nps-ups-result-help"
        onClick={() => setShowResult((s) => !s)}
        aria-label={showResult ? 'Show explanation' : 'Show number'}
      >
        {showResult ? '?' : '×'}
      </button>

      {showResult ? (
        <>
          <h4>Loan amount</h4>
          <h1 className="nps-ups-years-figure engager-money-figure">{loanDisplay}</h1>
          {wordsLine ? (
            <p className="calc-output-amount-in-words">{wordsLine}</p>
          ) : null}
        </>
      ) : (
        <>
          <h4 className="calc-wtm-heading">What this means</h4>
          <CalculatorWhatThisMeans
            regionLabel="Explanation of your loan amount result"
            heroValue={loanDisplay}
            heroVariant="inr"
            tagline={
              <p>
                <strong>Principal</strong> supported by this EMI, rate, and tenure using the
                standard amortisation formula.
              </p>
            }
            highlight={
              <>
                Assumes <em>fixed rate</em> and steady payments—actual eligibility may differ.
              </>
            }
            highlightTone="warning"
            foot="Illustrative only—not advice."
          />
        </>
      )}
    </div>
  );
}
